import { useEffect, useId, useRef } from "react";
import { CalendarDays, CircleHelp, Clock3, FileText, MapPinned, Sparkles, X } from "lucide-react";
import type { CatalogEvent } from "../types";

export function Overlay({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div className="overlay-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <section className="overlay-card" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header><h2 id={titleId}>{title}</h2><button ref={closeRef} onClick={onClose} aria-label="Schließen"><X size={20} /></button></header>
        {children}
      </section>
    </div>
  );
}

export function EventConfirmation({
  event,
  busy,
  error,
  onClose,
  onConfirm,
  onOpenExisting,
}: {
  event: CatalogEvent;
  busy: boolean;
  error?: string;
  onClose: () => void;
  onConfirm: () => void;
  onOpenExisting?: () => void;
}) {
  const refreshing = event.map_status === "stale";
  return (
    <Overlay title={refreshing ? "Karte aktualisieren?" : "Karte jetzt erstellen?"} onClose={onClose}>
      <div className="confirm-content">
        <div className="confirm-event">
          <span className="confirm-calendar"><CalendarDays size={22} /></span>
          <div><span>{event.city_name}</span><strong>{event.locality}</strong><small>{formatEventDate(event)}{formatTime(event)}</small></div>
        </div>
        <div className="confirm-source"><FileText size={18} /><span><strong>{refreshing ? "Ein neuer Tourplan liegt vor" : "Offizieller Tourplan liegt vor"}</strong>{refreshing ? "Die vorhandene Karte bleibt verfügbar, bis die aktualisierte Version fertig ist." : "Das PDF wird von hofflohmaerkte.de geladen und einmalig georeferenziert."}</span></div>
        <p className="confirm-explainer"><Sparkles size={17} /> {refreshing ? "Nach erfolgreicher Prüfung ersetzt die neue Version automatisch die bisherige Event-Karte." : "Danach steht die fertige Karte für alle zukünftigen Aufrufe dieses Termins direkt bereit."}</p>
        {error && <div className="inline-error" role="alert">{error}</div>}
        <div className="confirm-actions">
          <button className="button button-secondary" onClick={onOpenExisting || onClose} disabled={busy}>{refreshing ? "Bisherige öffnen" : "Abbrechen"}</button>
          <button className="button button-primary" onClick={onConfirm} disabled={busy}><MapPinned size={17} /> {busy ? "Wird gestartet …" : refreshing ? "Aktualisieren" : "Karte erstellen"}</button>
        </div>
      </div>
    </Overlay>
  );
}

export function HelpOverlay({ onClose }: { onClose: () => void }) {
  return (
    <Overlay title="So funktioniert’s" onClose={onClose}>
      <div className="help-content">
        <span className="help-hero"><CircleHelp size={26} /></span>
        <p>Wähle zuerst einen veröffentlichten Tourplan aus der Terminübersicht. Ist er schon verarbeitet, öffnet sich die Karte sofort.</p>
        <ol>
          <li><strong>Termin auswählen</strong><span>Verfügbare PDFs sind direkt an ihrem Status erkennbar.</span></li>
          <li><strong>Automatisch abgleichen</strong><span>Pinke Hofpunkte sowie U- und S-Bahn-Referenzen werden erkannt.</span></li>
          <li><strong>Öffnen oder exportieren</strong><span>Die Karte bleibt am Event verknüpft; KML, CSV und GeoJSON stehen bereit.</span></li>
        </ol>
        <div className="help-note"><Clock3 size={16} /> Eigene Uploads bleiben 30 Tage gespeichert. Jeder Aufruf des persönlichen Links startet diese Frist neu.</div>
      </div>
    </Overlay>
  );
}

function formatEventDate(event: CatalogEvent) {
  return new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).format(new Date(`${event.event_date}T12:00:00`));
}

function formatTime(event: CatalogEvent) {
  if (!event.start_time || !event.end_time) return "";
  return ` · ${event.start_time.slice(0, 5)}–${event.end_time.slice(0, 5)} Uhr`;
}
